import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import moment from 'moment-timezone';
import { parseAppointmentDate, toAppointmentUTC } from '../../utils/appointmentDate';
import TableDocuments from './TableDocuments';
import ReviewProfile from './ReviewProfile';
import apiClient from '../../utils/axiosPetition';
import { alertaGeneral } from '../../utils/alertas';
import { useDataUser } from '../../context/dataUserContext';
import Contenedor from '../GlobalComponents/Contenedor';
import SvgIcon from '../../utils/Icon';
import { ReactComponent as Buscar } from '../../assets/icons/lupa.svg';
import { ReactComponent as Afiliados } from "../../assets/icons/Afiliados.svg";
import { ReactComponent as Perfil } from "../../assets/icons/perfilUsuario.svg";
import { ReactComponent as Calendario } from "../../assets/icons/calendario.svg";
import { ReactComponent as EditarIcon } from "../../assets/icons/Preescribir.svg";
import { ReactComponent as AgendarCitaIcon } from "../../assets/icons/citas.svg";
import { ReactComponent as Password } from "../../assets/icons/contraseña.svg";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import InputFile from '../../utils/InputFile';
import { fetchProfessionsFlat } from '../../utils/professionService';
import { formatServiceName } from '../../utils/formatServiceName';
import Modal from 'react-modal';
import Cargando from '../../utils/Cargando';
import Swal from 'sweetalert2';
Modal.setAppElement('#root');

/** ePayco clínico (Sí a servicios) o ePayco marketplace (perfil experto / REDPLIKAPLACE) pendiente de confirmación admin */
const isPendienteEpayco = (doctor) =>
    doctor?.estado === 'pendiente_epayco' || Boolean(doctor?.epaycoPendienteConfirmacionAdmin);

const isPendienteEpaycoMarketplace = (doctor) =>
    Boolean(doctor?.epaycoPendienteConfirmacionAdmin) && doctor?.estado !== 'pendiente_epayco';

/** Etiqueta visible del tipo de perfil profesional */
const getPerfilLabel = (doctor) => {
    if (doctor?.tipoPerfil === 'experto' || doctor?.prestacionServicio === false) {
        return {
            label: 'Experto',
            detail: 'Marketplace',
            className: 'bg-orange-100 text-orange-800',
        };
    }
    if (doctor?.tipoPerfil === 'asistencial' || doctor?.prestacionServicio === true) {
        return {
            label: 'Asistencial',
            detail: 'Citas clínicas',
            className: 'bg-sky-100 text-sky-800',
        };
    }
    return {
        label: 'Sin definir',
        detail: null,
        className: 'bg-slate-100 text-slate-600',
    };
};

const DoctorComponent = () => {
    const { clinicId } = useDataUser();
    const [showForm, setShowForm] = useState(false);
    const [doctors, setDoctors] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [showCalendar, setShowCalendar] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [patients, setPatients] = useState([]);
    const [showAppointmentModal, setShowAppointmentModal] = useState(false);
    const [appointmentNotes, setAppointmentNotes] = useState('');
    const [appointmentDate, setAppointmentDate] = useState('');
    const [schedulingAppointment, setSchedulingAppointment] = useState(false);
    const [appointments, setAppointments] = useState([]); // Estado para almacenar las citas del paciente
    const [loadingAppointments, setLoadingAppointments] = useState(true);
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [showProfile, setShowProfile] = useState(false);
    const [filesToEdit, setFilesToEdit] = useState([]); // Variable para manejar los archivos que se van a subir
    const [activeTab, setActiveTab] = useState("En Revisión");


    const [professionFilters, setProfessionFilters] = useState([
        { label: 'Profesionales', value: 'todos' },
    ]);
    const [professionOptions, setProfessionOptions] = useState([]);

    const [selectedProfession, setSelectedProfession] = useState("todos");
    const [filteredDoctors, setFilteredDoctors] = useState([]);
    const [showReviewProfile, setShowReviewProfile] = useState(false);

    // Flujo principal: Revisión → Pendiente ePayco → Aprobados. Otros estados se muestran aparte.
    const stepsTabs = ["En Revisión", "Pendiente ePayco", "Aprobados"];
    const otherTabs = ["No Aprobados", "Prestación inhabilitada"];
    const tabs = [...stepsTabs, ...otherTabs];

    const selectStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: 42,
            borderRadius: 12,
            borderColor: state.isFocused ? '#1f94c2' : '#cbd5e1',
            boxShadow: state.isFocused ? '0 0 0 3px rgba(31,148,194,0.18)' : 'none',
            '&:hover': {
                borderColor: '#1f94c2'
            }
        }),
        valueContainer: (base) => ({ ...base, paddingLeft: 12, paddingRight: 12 }),
        indicatorsContainer: (base) => ({ ...base, paddingRight: 8 }),
        menu: (base) => ({ ...base, zIndex: 80 }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#1f94c2' : state.isFocused ? 'rgba(31,148,194,0.12)' : '#ffffff',
            color: state.isSelected ? '#ffffff' : '#0f172a',
            fontWeight: state.isSelected ? 700 : 500
        }),
        singleValue: (base) => ({ ...base, color: '#0f172a', fontWeight: 700 }),
        placeholder: (base) => ({ ...base, color: '#64748b' })
    };

    const tabCounts = (() => {
        const searchTermLower = searchTerm.toLowerCase();
        const matchesTab = (doctor, tab) => {
            // Aprobados: perfil OK y sin ePayco pendiente (clínico o marketplace)
            if (tab === "Aprobados") return doctor.estado === "aprobado" && !doctor.epaycoPendienteConfirmacionAdmin;
            if (tab === "Pendiente ePayco") return isPendienteEpayco(doctor);
            if (tab === "No Aprobados") return doctor.estado === "no_aprobado";
            if (tab === "En Revisión") return doctor.estado === "en_revision";
            if (tab === "Prestación inhabilitada") return doctor.estado === "rechazado";
            return false;
        };

        return tabs.reduce((acc, tab) => {
            const count = doctors.filter((doctor) => {
                const matchesService = selectedProfession !== "todos" ? doctor.service === selectedProfession : true;
                const matchesSearch =
                    doctor.fullName?.toLowerCase().includes(searchTermLower) ||
                    doctor.email?.toLowerCase().includes(searchTermLower) ||
                    doctor.cellNumber?.toLowerCase().includes(searchTermLower) ||
                    doctor.idNumber?.toLowerCase().includes(searchTermLower);
                return matchesService && matchesSearch && matchesTab(doctor, tab);
            }).length;
            acc[tab] = count;
            return acc;
        }, {});
    })();



    const fetchDoctors = async () => {
        try {
            const response = await apiClient.get(`/api/clinic/getDoctors/${clinicId}`);
            setDoctors(response.data);
        } catch (error) {
            console.error('Error fetching doctors:', error);
        }
    };


    useEffect(() => {
        const searchTermLower = searchTerm.toLowerCase();

        const filtered = doctors.filter((doctor) => {
            const matchesService = selectedProfession !== "todos" ? doctor.service === selectedProfession : doctor; // Filtra por service
            const matchesStatus =
                (activeTab === "Aprobados" && doctor.estado === "aprobado" && !doctor.epaycoPendienteConfirmacionAdmin) ||
                (activeTab === "Pendiente ePayco" && isPendienteEpayco(doctor)) ||
                (activeTab === "No Aprobados" && doctor.estado === "no_aprobado") ||
                (activeTab === "En Revisión" && doctor.estado === "en_revision") ||
                (activeTab === "Prestación inhabilitada" && doctor.estado === "rechazado");
            const matchesSearch =
                doctor.fullName?.toLowerCase().includes(searchTermLower) ||
                doctor.email?.toLowerCase().includes(searchTermLower) ||
                doctor.cellNumber?.toLowerCase().includes(searchTermLower) ||
                doctor.idNumber?.toLowerCase().includes(searchTermLower);

            return matchesService && matchesStatus && matchesSearch;
        });

        setFilteredDoctors(filtered);
    }, [doctors, selectedProfession, searchTerm, activeTab]);


    const fetchPatients = async () => {
        try {
            const response = await apiClient.get(`/api/clinic/getPatients/${clinicId}`);
            setPatients(response.data);
        } catch (error) {
            console.error('Error fetching patients:', error);
        }
    };

    const fetchAppointments = async () => {
        try {
            setLoadingAppointments(true);
            const response = await apiClient.get(`/api/clinic/getAppointmentsDoctor/${selectedDoctor._id}`);

            console.log('Datos de citas recibidos:', response.data);
            const appointmentsData = response.data.map(appointment => {
                const citaDate = parseAppointmentDate(appointment.date);
                return {
                    title: 'Cita de control',
                    start: citaDate.toISOString(),
                    allDay: false,
                    backgroundColor: '#000aff',
                    borderColor: '#000aff',
                    extendedProps: {
                        notes: appointment.notes,
                        startTime: citaDate.format('HH:mm'),
                        patientName: `${appointment.patient.firstName} ${appointment.patient.firstLastName}`
                    }
                };
            });
            console.log('Datos de citas procesados:', appointmentsData);

            setAppointments(appointmentsData);
        } catch (error) {
            console.error('Error fetching appointments:', error);
            alertaGeneral({
                titulo: "Error",
                messageHtml: "No se pudieron cargar las citas del doctor.",
                isError: true
            });
        } finally {
            setLoadingAppointments(false);
        }
    };

    useEffect(() => {
        if (!selectedDoctor || !showCalendar) return;
        fetchAppointments();
    }, [showCalendar, selectedDoctor]);

    useEffect(() => {
        fetchDoctors();
        fetchPatients();
        fetchProfessionsFlat().then((flat) => {
            setProfessionOptions(flat);
            setProfessionFilters([
                { label: 'Profesionales', value: 'todos' },
                ...flat.map((p) => ({ label: p.label, value: p.value })),
            ]);
        });
    }, []);

    const handleShowForm = () => {
        setShowForm(true);
    };

    const handleCancel = () => {
        setShowAppointmentModal(false);
        setSchedulingAppointment(false);
        setSelectedDoctor(null);
    };

    const handleGoBack = () => {
        setSelectedPatient(null);
        setAppointments([]); // Limpia las citas al volver
        setShowForm(false);
        setShowCalendar(false); // Oculta el calendario al volver
        setShowChangePassword(false);
        setShowProfile(false);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleEventClick = (info) => {
        setSelectedEvent(info.event);
    };

    const handleCloseModal = () => {
        setSelectedEvent(null);
    };

    const filtereddoctors = doctors.filter(doctor => {
        const searchTermLower = searchTerm.toLowerCase();
        return (
            doctor.fullName?.toLowerCase().includes(searchTermLower) ||
            doctor.email?.toLowerCase().includes(searchTermLower) ||
            doctor.cellNumber?.toLowerCase().includes(searchTermLower) ||
            doctor.idNumber?.toLowerCase().includes(searchTermLower)
        );
    });

    const toggleMenu = (doctor) => {
        setSelectedDoctor(doctor);
    };

    const handleMenuOptionClick = (option) => {


        if (option === 'Eliminar Doctor') {
            handleDeletedoctor();
        }
        if (option === 'Calendario') {
            setShowCalendar(true);
        }
        if (option === 'Agendar Cita') {
            setShowAppointmentModal(true); // Muestra el modal de agendar cita
        }
        if (option === 'Cambiar Contraseña') {
            setShowChangePassword(true);
        }
        if (option === 'Revisar') {
            setShowReviewProfile(true);
        }
        if (option === 'Perfil') {
            setShowProfile(true);
        }
    };

    const handleDeletedoctor = async () => {
        try {
            alertaGeneral({
                titulo: "¿Estás seguro?",
                messageHtml: "Una vez eliminado, no podrás recuperar esta información.",
                confirmButtonText: "Si, eliminar",
                cancelButton: true,
                cancelButtonText: "Cancelar",
                isWarning: true,
            }).then(async (result) => {
                if (result.isConfirmed) {
                    const response = await apiClient.put(`/api/clinic/deleteDoctor/${selectedDoctor._id}`);

                    if (response.status === 200) {
                        alertaGeneral({
                            titulo: "Eliminado",
                            messageHtml: "El doctor ha sido eliminado exitosamente.",
                        });
                        setDoctors(doctors.filter(doctor => doctor._id !== selectedDoctor._id));
                    } else {
                        alertaGeneral({
                            titulo: "Error",
                            messageHtml: "No se pudo eliminar el doctor. Inténtalo de nuevo.",
                            isError: true
                        });
                    }
                }
            })
        } catch (error) {
            console.error('Error deleting doctor:', error);
            alertaGeneral({
                titulo: "Error",
                messageHtml: "Ocurrió un error al eliminar el doctor.",
                isError: true
            });
        }
    };

    const handleScheduleAppointment = async () => {
        if (schedulingAppointment || !selectedPatient) return;
        setSchedulingAppointment(true);
        try {
            const formattedDate = toAppointmentUTC(appointmentDate);

            const response = await apiClient.post(
                `/api/clinic/scheduleAppointment`,
                {
                    patientId: selectedPatient,
                    date: formattedDate,
                    notes: appointmentNotes,
                    doctorId: selectedDoctor._id, // Enviando el ID del doctor
                    service: selectedDoctor.service, // Enviando el servicio seleccionado
                }
            );

            if (response.status === 200) {
                alertaGeneral({
                    titulo: "Cita Agendada",
                    messageHtml: "La cita ha sido agendada exitosamente.",
                })
                setShowAppointmentModal(false);
                setAppointmentDate('');
                setAppointmentNotes('');
                setSelectedPatient(null);
            } else {
                alertaGeneral({
                    titulo: "Error",
                    messageHtml: "No se pudo agendar la cita. Inténtalo de nuevo.",
                    isError: true
                });
            }
        } catch (error) {
            console.error('Error scheduling appointment:', error);
            const conflict = error.response?.status === 409;
            alertaGeneral({
                titulo: conflict ? 'Horario no disponible' : 'Error',
                messageHtml: error.response?.data?.message || 'No se pudo agendar la cita. Inténtalo de nuevo.',
                isError: true,
            });
        } finally {
            setSchedulingAppointment(false);
        }
    };


    const handleEditDoctor = async () => {
        try {
            const dataDoctorToSend = new FormData();

            // Agrega todos los campos de selectedDoctor
            for (const key in selectedDoctor) {
                const value = selectedDoctor[key];
                // Si el campo es un array u objeto (y no es null), conviértelo a string
                if (
                    (Array.isArray(value) || (typeof value === 'object' && value !== null)) &&
                    !(value instanceof File) // No convertir archivos a string
                ) {
                    dataDoctorToSend.append(key, JSON.stringify(value));
                } else if (value !== undefined && value !== null) {
                    dataDoctorToSend.append(key, value);
                }
            }

            // Agrega los archivos (si tienes un estado separado para archivos)
            for (const key in filesToEdit) {
                if (filesToEdit[key]) {
                    dataDoctorToSend.append(key, filesToEdit[key]);
                }
            }

            // No pongas Content-Type, axios/fetch lo hace solo
            const response = await apiClient.put(
                `/api/clinic/editDoctor/${selectedDoctor._id}`,
                dataDoctorToSend
            );

            if (response.status === 200) {
                alertaGeneral({
                    titulo: "Doctor Editado",
                    messageHtml: "El doctor ha sido editado exitosamente.",
                });
                setShowProfile(false);
                fetchDoctors();
            } else {
                alertaGeneral({
                    titulo: "Error",
                    messageHtml: "No se pudo editar el doctor. Inténtalo de nuevo.",
                    isError: true
                });
            }
        } catch (error) {
            alertaGeneral({
                titulo: "Error",
                messageHtml: "Ocurrió un error al editar el doctor.",
                isError: true
            });
        }
    };

    const lstDocumentsExists = [
        { nameToSave: 'foto_perfil', name: 'Foto Profesional', url: selectedDoctor?.urlProfessionalPhoto || '', isEdit: true },
        { nameToSave: 'cedulaPDF', name: 'Documento Identidad', url: selectedDoctor?.urlIdDocument || '', isEdit: true },
        { nameToSave: 'diploma_acta_grado', name: 'Diploma Grado', url: selectedDoctor?.urlUndergraduateDiploma || '', isEdit: true },
        { nameToSave: 'diploma_especializacion', name: 'Diploma Especialización', url: selectedDoctor?.urlSpecializationDiploma || '', isEdit: true },
        { nameToSave: 'homologacion_estudios', name: 'Homologación Estudios', url: selectedDoctor?.urlStudyHomologation || '', isEdit: true },
        { nameToSave: 'tarjeta_profesional', name: 'Tarjeta Profesional', url: selectedDoctor?.urlProfessionalCard || '', isEdit: true },
        { nameToSave: 'firma_profesional', name: 'Firma Profesional', url: selectedDoctor?.urlProfessionalSignature || '', isEdit: true },
        { nameToSave: 'rethus', name: 'ReTHUS', url: selectedDoctor?.urlRethusDocument || '', isEdit: true },
        { nameToSave: 'poliza_responsabilidad_civil', name: 'Poliza Responsabilidad Civil', url: selectedDoctor?.urlCivilLiabilityPolicyDocument || '', isEdit: true },
    ];
    if (showReviewProfile) {
        return (
            <ReviewProfile
                doctor={selectedDoctor}
                onGoBack={() => setShowReviewProfile(false)}
                fetchDoctors={fetchDoctors}
            />
        );
    }

    if (showProfile) {
        return (
            <Contenedor title={`Editar perfil de ${selectedDoctor.fullName}`} icon={Perfil}>
                <div className="w-full">
                    <form>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Nombre Completo */}
                            <div>
                                <label className="form-label">Nombre Completo</label>
                                <input
                                    type="text"
                                    value={selectedDoctor.fullName}
                                    onChange={(e) => setSelectedDoctor({ ...selectedDoctor, fullName: e.target.value })}
                                    className="form-input"
                                />
                            </div>

                            {/* Tipo de ID */}
                            <div>
                                <label className="form-label">Tipo de ID</label>
                                <select
                                    value={selectedDoctor.idType}
                                    onChange={(e) => setSelectedDoctor({ ...selectedDoctor, idType: e.target.value })}
                                    className="form-input"
                                >

                                    <option value="CC">CC</option>
                                    <option value="TI">TI</option>
                                    <option value="CE">CE</option>
                                    <option value="PS">PS</option>
                                    <option value="DE">DE</option>
                                </select>
                            </div>

                            {/* Número de ID */}
                            <div>
                                <label className="form-label">Número de ID</label>
                                <input
                                    type="text"
                                    value={selectedDoctor.idNumber}
                                    onChange={(e) => setSelectedDoctor({ ...selectedDoctor, idNumber: e.target.value })}
                                    className="form-input"
                                />
                            </div>

                            {/* Correo Electrónico */}
                            <div>
                                <label className="form-label">Correo Electrónico</label>
                                <input
                                    type="email"
                                    value={selectedDoctor.email}
                                    onChange={(e) => setSelectedDoctor({ ...selectedDoctor, email: e.target.value })}
                                    className="form-input"
                                />
                            </div>

                            {/* Número de Teléfono */}
                            <div>
                                <label className="form-label">Número de Teléfono</label>
                                <input
                                    type="tel"
                                    value={selectedDoctor.cellNumber}
                                    onChange={(e) => setSelectedDoctor({ ...selectedDoctor, cellNumber: e.target.value })}
                                    className="form-input"
                                />
                            </div>

                            {/* Servicio */}
                            <div>
                                <label className="form-label">Servicio</label>
                                <select
                                    value={selectedDoctor.service}
                                    onChange={(e) => setSelectedDoctor({ ...selectedDoctor, service: e.target.value })}
                                    className="form-input"
                                >
                                    {!professionOptions.some((p) => p.value === selectedDoctor.service) && (
                                        <option value={selectedDoctor.service}>{selectedDoctor.service}</option>
                                    )}
                                    {professionOptions.map((p) => (
                                        <option key={p.value} value={p.value}>{p.label}</option>
                                    ))}
                                </select>
                            </div>

                        </div>

                        <div className="flex justify-end mt-4 gap-3">
                            <button
                                onClick={handleGoBack}
                                className="button-gray"
                            >
                                Volver
                            </button>
                            <button
                                type="button"
                                onClick={handleEditDoctor}
                                className="button-send"
                            >
                                Guardar cambios
                            </button>
                        </div>
                    </form>
                    {/* Tabla Visualizacion Y Descargas De Los Documentos */}
                    <TableDocuments
                        lstDocuments={lstDocumentsExists ? lstDocumentsExists : null}
                        filesToEdit={filesToEdit}
                        setFilesToEdit={setFilesToEdit}
                    />
                </div>
            </Contenedor>
        );
    }



    if (showCalendar) {
        return (
            <Contenedor icon={Calendario} title={`Calendario de ${selectedDoctor.fullName}`} >
                <div className="flex justify-end mb-4">
                    <button className="button-gray" onClick={handleGoBack}>
                        Volver
                    </button>
                </div>
                {loadingAppointments && true ? (
                    <Cargando />
                ) : (
                    <>
                        <main className="flex-grow flex items-center justify-center">
                            <div className="w-full max-w-3xl bg-white">
                                <FullCalendar
                                    plugins={[dayGridPlugin, interactionPlugin]}
                                    initialView="dayGridMonth"
                                    events={appointments}
                                    locales={[esLocale]}
                                    locale="es"
                                    timeZone="local"
                                    dayCellClassNames={(date) =>
                                        date.date.getDate() === new Date().getDate() &&
                                            date.date.getMonth() === new Date().getMonth()
                                            ? 'current-day'
                                            : ''
                                    }
                                    eventContent={(eventInfo) => {
                                        const startTime = parseAppointmentDate(eventInfo.event.start).format('h:mm A');
                                        return (
                                            <div>
                                                <div className="font-semibold text-white text-xs">{eventInfo.event.title}</div>
                                                <div className=" text-white text-xs ">{startTime}</div>
                                            </div>
                                        );
                                    }}
                                    eventClick={(info) => handleEventClick(info)}
                                />
                            </div>
                        </main>

                        {selectedEvent && (
                            <Modal
                                isOpen={selectedEvent}
                                onRequestClose={handleCloseModal}
                                className="w-[300px] mx-auto bg-white p-4 rounded-lg shadow-lg text-center border border-[#6e787aff] focus:outline-none"
                                overlayClassName="fixed inset-0 flex items-center justify-center bg-white bg-opacity-75 z-[20]"
                            >
                                <h2 className="text-2xl text-[#205087ff] font-bold">{selectedEvent.title}</h2>
                                <hr className="h-[1px] w-full bg-[#205087ff] mb-2" />

                                <div className="space-y-2">
                                    <p className="text-left text-[#70706eff] font-semibold">
                                        Fecha: <span className="font-medium">{parseAppointmentDate(selectedEvent.start).format('YYYY-MM-DD')}</span>
                                    </p>
                                    <p className="text-left text-[#70706eff] font-semibold">
                                        Hora: <span className="font-medium">{parseAppointmentDate(selectedEvent.start).format('HH:mm')}</span>
                                    </p>
                                    <p className="text-left text-[#70706eff] font-semibold">
                                        Paciente: <span className="font-medium">{selectedEvent.extendedProps.patientName}</span>
                                    </p>
                                    <p className="text-left text-[#70706eff] font-semibold">
                                        Notas: <span className="font-medium">{selectedEvent.extendedProps.notes}</span>
                                    </p>
                                </div>
                                <div className="flex justify-end mt-2">
                                    <button
                                        onClick={handleCloseModal}
                                        className="button-gray"
                                    >
                                        Cerrar
                                    </button>
                                </div>
                            </Modal>
                        )}
                        <style>
                            {`
                    .fc-event {
                        background-color: #1f94c2ff !important;
                        border: 1px solid #3090b3ff;
                        padding: 2px;
                        cursor: pointer !important;
                    }
                    .fc-daygrid-day {
                        visibility: visible;
                    }
                    .current-day {
                        background-color: #e7e6e6 !important;
                        border: 2px solid #fbc02d;
                    }
                    `}
                        </style>
                    </>
                )}
            </Contenedor>
        );
    }

    const closeChangePasswordModal = () => {
        setShowChangePassword(false);
    };

    const handleChangePassword = async () => {
        try {

            const response = await apiClient.post(`/api/clinic/changePasswordpatient`, {
                username: selectedDoctor.email,
                newPassword: newPassword
            });

            if (response.status === 200) {
                alertaGeneral({
                    titulo: "Contraseña cambiada con éxito"
                });
                setNewPassword('');
                closeChangePasswordModal();
            } else {
                alertaGeneral({
                    titulo: "Error",
                    messageHtml: "Contraseña actual incorrecta.",
                    isError: true
                });
            }

        } catch (error) {
            alertaGeneral({
                titulo: "Ocurrio un error inesperado",
                isError: true
            });
        }
    };

    return (
        <>
            {!showForm ? (
                <Contenedor icon={Afiliados} title={professionFilters.find((profession) => profession.value === selectedProfession)?.label || selectedProfession}>
                    <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3 mb-5">
                        <div className="flex flex-col md:flex-row md:items-center gap-3 w-full">
                            <div className="w-full md:w-[340px]">
                                <Select
                                    options={professionFilters}
                                    value={professionFilters.find((profession) => profession.value === selectedProfession)}
                                    onChange={(selectedOption) => setSelectedProfession(selectedOption.value)}
                                    menuPlacement="auto"
                                    styles={selectStyles}
                                    classNamePrefix="select"
                                />
                            </div>

                            <div className="relative w-full md:w-[340px]">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1f94c2ff]">
                                    <SvgIcon Icon={Buscar} className={"w-5 h-5"} />
                                </span>
                                <input
                                    type="text"
                                    placeholder="Buscar profesional"
                                    className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#1f94c2ff]/30 focus:border-[#1f94c2ff]"
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                />
                            </div>
                        </div>

                        {selectedProfession !== "todos" && (
                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={handleShowForm}
                                    className="inline-flex items-center gap-2.5 px-5 py-3 rounded-xl bg-gradient-to-r from-[#1f94c2] to-[#205087] text-white font-semibold text-sm shadow-md hover:shadow-lg hover:from-[#1a7fa8] hover:to-[#1a4570] active:scale-[0.98] transition-all duration-200 border border-[#1f94c2]/20"
                                >
                                    <FontAwesomeIcon icon={faUserPlus} className="w-4 h-4" />
                                    <span>Agregar {professionFilters.find((profession) => profession.value === selectedProfession)?.label || selectedProfession}</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Pestañas compactas: una sola fila, fácil de escanear */}
                    <div className="bg-white border border-slate-200 rounded-lg px-2 py-1.5 shadow-sm mb-3">
                        <div className="flex flex-wrap items-center gap-1">
                            {stepsTabs.map((tab, index) => {
                                const isActive = activeTab === tab;
                                const count = tabCounts?.[tab] ?? 0;
                                return (
                                    <button
                                        key={tab}
                                        type="button"
                                        onClick={() => setActiveTab(tab)}
                                        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                                            isActive
                                                ? 'bg-[#205087] text-white'
                                                : 'text-slate-600 hover:bg-slate-100'
                                        }`}
                                    >
                                        <span className={isActive ? 'text-white/90 font-bold tabular-nums' : 'text-slate-400 font-bold tabular-nums'}>{index + 1}.</span>
                                        <span className="whitespace-nowrap">{tab}</span>
                                        <span className={`tabular-nums font-semibold ${isActive ? 'text-white/90' : 'text-slate-500'}`}>({count})</span>
                                    </button>
                                );
                            })}
                            <span className="w-px h-4 bg-slate-200 mx-0.5" aria-hidden />
                            {otherTabs.map((tab) => {
                                const isActive = activeTab === tab;
                                const count = tabCounts?.[tab] ?? 0;
                                return (
                                    <button
                                        key={tab}
                                        type="button"
                                        onClick={() => setActiveTab(tab)}
                                        className={`inline-flex items-center gap-1 px-2 py-1.5 rounded-md text-xs transition-colors ${
                                            isActive ? 'bg-slate-200 text-slate-800 font-medium' : 'text-slate-500 hover:bg-slate-50'
                                        }`}
                                    >
                                        <span className="whitespace-nowrap">{tab}</span>
                                        <span className="tabular-nums font-medium">({count})</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                        <div className="overflow-auto max-h-[560px]">
                            <table className="w-full text-sm">
                                <thead className="sticky top-0 z-[1] bg-slate-50">
                                    <tr className="text-slate-900">
                                        <th className="text-left px-3 py-2.5 font-semibold whitespace-nowrap">Nombre</th>
                                        <th className="text-left px-3 py-2.5 font-semibold whitespace-nowrap">Perfil</th>
                                        {selectedProfession === "todos" && <th className="text-left px-3 py-2.5 font-semibold whitespace-nowrap">Servicio</th>}
                                        <th className="text-left px-3 py-2.5 font-semibold whitespace-nowrap">Correo</th>
                                        <th className="text-left px-3 py-2.5 font-semibold whitespace-nowrap">Celular</th>
                                        <th className="text-left px-3 py-2.5 font-semibold whitespace-nowrap">Tipo ID</th>
                                        <th className="text-left px-3 py-2.5 font-semibold whitespace-nowrap">Identificación</th>
                                        <th className="text-left px-3 py-2.5 font-semibold whitespace-nowrap min-w-[200px]">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200">
                                    {filteredDoctors.length === 0 ? (
                                        <tr>
                                            <td colSpan={selectedProfession === "todos" ? 8 : 7} className="px-4 py-10">
                                                <div className="flex flex-col items-center justify-center text-center">
                                                    <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-3 ring-4 ring-slate-50">
                                                        <SvgIcon Icon={Afiliados} className="w-7 h-7 text-[#1f94c2ff]" />
                                                    </div>
                                                    <p className="text-slate-700 font-semibold">No hay profesionales para mostrar</p>
                                                    <p className="text-slate-500 text-sm mt-1">Prueba cambiando el filtro o la búsqueda.</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredDoctors.map((doctor) => {
                                            const perfil = getPerfilLabel(doctor);
                                            return (
                                            <tr key={doctor._id} className="bg-white hover:bg-[#1f94c2ff]/5 transition-colors">
                                                <td className="px-3 py-2.5 font-semibold text-slate-900 whitespace-nowrap">
                                                    <span className="inline-flex items-center gap-2 flex-wrap">
                                                        {doctor.fullName}
                                                        {doctor.epaycoPendienteConfirmacionAdmin && (
                                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold">
                                                                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                                                                {isPendienteEpaycoMarketplace(doctor) ? 'ID marketplace' : 'ID reportado'}
                                                            </span>
                                                        )}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2.5 whitespace-nowrap">
                                                    <span className={`inline-flex flex-col items-start gap-0.5 px-2.5 py-1 rounded-lg text-[11px] font-bold ${perfil.className}`}>
                                                        <span>{perfil.label}</span>
                                                        {perfil.detail && (
                                                            <span className="text-[10px] font-semibold normal-case opacity-80">{perfil.detail}</span>
                                                        )}
                                                    </span>
                                                </td>
                                                {selectedProfession === "todos" && (
                                                    <td className="px-3 py-2.5 whitespace-nowrap text-slate-800">{formatServiceName(doctor.service)}</td>
                                                )}
                                                <td className="px-3 py-2.5 whitespace-nowrap text-slate-800">{doctor.email}</td>
                                                <td className="px-3 py-2.5 whitespace-nowrap text-slate-800">{doctor.cellNumber}</td>
                                                <td className="px-3 py-2.5 whitespace-nowrap uppercase text-slate-800">{doctor.idType}</td>
                                                <td className="px-3 py-2.5 whitespace-nowrap text-slate-800">{doctor.idNumber}</td>
                                                <td className="px-3 py-2.5 whitespace-nowrap">
                                                    {isPendienteEpaycoMarketplace(doctor) ? (
                                                        <div className="inline-flex items-center gap-2 flex-wrap">
                                                            <a
                                                                href="https://dashboard.epayco.com/login"
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#205087ff] hover:bg-[#1a4069] text-white font-semibold shadow-sm transition-colors"
                                                            >
                                                                Ir a ePayco
                                                            </a>
                                                            <button
                                                                type="button"
                                                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#1f94c2ff] hover:bg-[#205087ff] text-white font-semibold shadow-sm transition-colors"
                                                                onClick={async () => {
                                                                    const { value: epaycoId, isConfirmed } = await Swal.fire({
                                                                        title: `Confirmar ePayco marketplace — ${doctor.fullName}`,
                                                                        html: '<p class="text-sm text-slate-600 mb-2">Perfil experto / REDPLIKAPLACE. Confirma que el ID está matriculado en split.</p>',
                                                                        input: 'text',
                                                                        inputLabel: 'ID de cuenta ePayco',
                                                                        inputValue: doctor.epaycoAliadoId || '',
                                                                        showCancelButton: true,
                                                                        confirmButtonText: 'Confirmar cuenta',
                                                                        cancelButtonText: 'Cancelar',
                                                                        inputValidator: (v) => (!v || !String(v).trim() ? 'El ID de ePayco es requerido' : undefined),
                                                                    });
                                                                    if (!isConfirmed) return;
                                                                    try {
                                                                        await apiClient.put(`/api/marketplace/gestion/vendedores/${doctor._id}/confirmar-epayco`, {
                                                                            epaycoAliadoId: String(epaycoId).trim(),
                                                                        });
                                                                        await fetchDoctors();
                                                                        Swal.fire('Confirmada', 'El vendedor ya puede publicar en REDPLIKAPLACE.', 'success');
                                                                    } catch (err) {
                                                                        Swal.fire('Error', err.response?.data?.message || 'No se pudo confirmar la cuenta', 'error');
                                                                    }
                                                                }}
                                                            >
                                                                <SvgIcon Icon={EditarIcon} className="w-4 h-4 text-white" />
                                                                Confirmar ePayco marketplace
                                                            </button>
                                                        </div>
                                                    ) : doctor.estado === "pendiente_epayco" ? (
                                                        <div className="inline-flex items-center gap-2 flex-wrap">
                                                            <a
                                                                href="https://dashboard.epayco.com/login"
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#205087ff] hover:bg-[#1a4069] text-white font-semibold shadow-sm transition-colors"
                                                            >
                                                                Ir a ePayco
                                                            </a>
                                                            <button
                                                                type="button"
                                                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#1f94c2ff] hover:bg-[#205087ff] text-white font-semibold shadow-sm transition-colors"
                                                                onClick={() => {
                                                                    toggleMenu(doctor);
                                                                    handleMenuOptionClick('Revisar');
                                                                }}
                                                            >
                                                                <SvgIcon Icon={EditarIcon} className="w-4 h-4 text-white" />
                                                                Confirmar registro ePayco
                                                            </button>
                                                        </div>
                                                    ) : doctor.estado === "en_revision" ? (
                                                        <button
                                                            type="button"
                                                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#1f94c2ff] hover:bg-[#205087ff] text-white font-semibold shadow-sm transition-colors"
                                                            onClick={() => {
                                                                toggleMenu(doctor);
                                                                handleMenuOptionClick('Revisar');
                                                            }}
                                                        >
                                                            <SvgIcon Icon={EditarIcon} className="w-4 h-4 text-white" />
                                                            Revisar
                                                        </button>
                                                    ) : (
                                                        <div className="inline-flex items-center gap-1.5 flex-nowrap">
                                                            <button
                                                                type="button"
                                                                className="w-8 h-8 inline-flex items-center justify-center rounded-lg bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
                                                                title="Editar"
                                                                onClick={() => {
                                                                    toggleMenu(doctor);
                                                                    handleMenuOptionClick('Perfil');
                                                                }}
                                                            >
                                                                <SvgIcon Icon={EditarIcon} className="w-4 h-4 text-[#1f94c2ff]" />
                                                            </button>

                                                            <button
                                                                type="button"
                                                                className="w-8 h-8 inline-flex items-center justify-center rounded-lg bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
                                                                title="Cambiar contraseña"
                                                                onClick={() => {
                                                                    toggleMenu(doctor);
                                                                    handleMenuOptionClick('Cambiar Contraseña');
                                                                }}
                                                            >
                                                                <SvgIcon Icon={Password} className="w-4 h-4 text-[#205087ff]" />
                                                            </button>

                                                            <button
                                                                type="button"
                                                                className="w-8 h-8 inline-flex items-center justify-center rounded-lg bg-white border border-slate-200 hover:bg-red-50 transition-colors"
                                                                title="Eliminar"
                                                                onClick={() => {
                                                                    toggleMenu(doctor);
                                                                    handleMenuOptionClick('Eliminar Doctor');
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={faTrashAlt} className="w-4 h-4 text-red-600" />
                                                            </button>

                                                            {(doctor.service?.toLowerCase().includes("medicina") ||
                                                                doctor.service?.toLowerCase().includes("enfermería") ||
                                                                doctor.service?.toLowerCase().includes("psicología") ||
                                                                doctor.service?.toLowerCase().includes("nutrición") ||
                                                                doctor.service?.toLowerCase().includes("terapia física") ||
                                                                doctor.service?.toLowerCase().includes("fisioterapeuta") ||
                                                                doctor.service?.toLowerCase().includes("terapeuta respiratorio") ||
                                                                doctor.service?.toLowerCase().includes("terapeuta ocupacional") ||
                                                                doctor.service?.toLowerCase().includes("fonoaudiología")) && (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        className="w-8 h-8 inline-flex items-center justify-center rounded-lg bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
                                                                        title="Ver calendario"
                                                                        onClick={() => {
                                                                            toggleMenu(doctor);
                                                                            handleMenuOptionClick('Calendario');
                                                                        }}
                                                                    >
                                                                        <SvgIcon Icon={Calendario} className="w-4 h-4 text-[#205087ff]" />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="w-8 h-8 inline-flex items-center justify-center rounded-lg bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
                                                                        title="Agendar cita"
                                                                        onClick={() => {
                                                                            toggleMenu(doctor);
                                                                            handleMenuOptionClick('Agendar Cita');
                                                                        }}
                                                                    >
                                                                        <SvgIcon Icon={AgendarCitaIcon} className="w-4 h-4 text-[#1f94c2ff]" />
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Contenedor>
            ) : (
                <Doctor
                    onGoBack={() => setShowForm(false)}
                    fetchDoctors={fetchDoctors}
                    initialService={selectedProfession}
                />
            )}

            {showChangePassword && (
                <Modal
                    isOpen={showChangePassword}
                    onRequestClose={closeChangePasswordModal}
                    className="w-[420px] mx-auto bg-white p-6 rounded-lg shadow-lg text-center border border-[#6e787aff] focus:outline-none"
                    overlayClassName="fixed inset-0 flex items-center justify-center bg-white bg-opacity-75 z-[20]"
                >
                    <h2 className="text-lg text-left text-[#205087ff] font-bold mb-4">CAMBIAR CONTRASEÑA PARA {selectedDoctor.email}</h2>
                    <div className="flex flex-col justify-start">
                        <div className="flex items-center mb-4 gap-2">
                            <label className="form-label text-nowrap">Nueva contraseña:</label>
                            <input
                                type="password"
                                className="py-1 form-input"
                                placeholder='******'
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="flex justify-center gap-3">
                        <button
                            className="button-send"
                            onClick={handleChangePassword}
                        >
                            Cambiar
                        </button>
                        <button
                            onClick={() => setShowChangePassword(false)}
                            className="button-gray"
                        >
                            Cancelar
                        </button>
                    </div>
                </Modal>
            )
            }

            {/* Modal de Agendar Cita */}
            {
                showAppointmentModal && (
                    <Modal
                        isOpen={showAppointmentModal}
                        onRequestClose={handleCancel}
                        className="w-[420px] mx-auto bg-white p-6 rounded-lg shadow-lg text-center border border-[#6e787aff] focus:outline-none"
                        overlayClassName="fixed inset-0 flex items-center justify-center bg-white bg-opacity-75 z-[20]"
                    >
                        <h2 className="text-lg text-[#205087ff] font-bold mb-4">
                            Agendar cita para {selectedDoctor.fullName}
                        </h2>

                        {/* Servicio del Doctor */}
                        <label className="text-left form-label mb-2">
                            Servicio:
                            <input
                                value={selectedDoctor.service}
                                readOnly
                                className="form-input"
                            />
                        </label>
                        <label className="text-left form-label mb-2 mt-4">
                            Selecciona paciente:
                            <Select
                                options={patients.map(patient => {
                                    const fullName = [patient.firstName, patient.secondName, patient.firstLastName, patient.secondLastName].filter(Boolean).join(' ').trim() || 'Sin nombre';
                                    return {
                                        value: patient._id,
                                        label: `${fullName} | Cédula: ${patient.idNumber || 'N/A'}`,
                                        patient
                                    };
                                })}
                                onChange={(selectedOption) => setSelectedPatient(selectedOption ? selectedOption.value : '')}
                                className="mt-1 block"
                                menuPlacement='auto'
                                placeholder="Buscar por nombre o cédula..."
                                filterOption={(option, inputValue) => {
                                    const p = option.patient;
                                    if (!p || !inputValue || !inputValue.trim()) return true;
                                    const search = inputValue.toLowerCase().trim();
                                    const fullName = [p.firstName, p.secondName, p.firstLastName, p.secondLastName].filter(Boolean).join(' ').toLowerCase();
                                    const cedula = (p.idNumber || '').toLowerCase();
                                    return fullName.includes(search) || cedula.includes(search);
                                }}
                            />
                        </label>

                        {/* Fecha de la cita */}
                        <label className="text-left form-label mb-2 mt-4">
                            Fecha de la cita:
                            <input
                                type="datetime-local"
                                value={appointmentDate}
                                onChange={(e) => setAppointmentDate(e.target.value)}
                                className="mt-1 form-input"
                            />
                        </label>

                        {/* Notas */}
                        <label className="text-left form-label mb-2 mt-4">
                            Motivo de la consulta:
                            <textarea
                                value={appointmentNotes}
                                onChange={(e) => setAppointmentNotes(e.target.value)}
                                className="form-input"
                                rows="4"
                                placeholder="Añadir Motivo de la consulta..."
                            />
                        </label>

                        <div className="flex justify-end mt-4 gap-3">
                            <button
                                onClick={handleCancel}
                                className="button-gray"
                                disabled={schedulingAppointment}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleScheduleAppointment}
                                className="button-send flex items-center justify-center gap-2 min-w-[100px]"
                                disabled={schedulingAppointment || !selectedPatient || !appointmentDate}
                            >
                                {schedulingAppointment ? (
                                    <>
                                        <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Agendando...
                                    </>
                                ) : (
                                    'Agendar'
                                )}
                            </button>
                        </div>
                    </Modal>
                )
            }


        </>
    );
};



const Doctor = ({ onGoBack, fetchDoctors, initialService }) => {
    const [departments, setDepartments] = useState([]);
    const [cities, setCities] = useState([]);
    const [formData, setFormData] = useState({
        fullName: '', // Nombre Completo
        idType: '', // Tipo de Documento de Identidad
        idNumber: '', // Número de Documento de Identidad
        cedulaPDF: '', // Documento Identidad (Adjuntar)
        email: '', // Correo Electrónico
        cellNumber: '', // Número de Celular
        diploma_acta_grado: '', // Diploma o Acta de Grado de Pregrado (PDF o Imagen)
        diploma_especializacion: '', // Diploma o Acta de Grado de Especialización
        homologacion_estudios: '', // Homologación de Estudios
        tarjeta_profesional: '', // Tarjeta Profesional (PDF o Imagen)
        rethus: '', // ReThus (PDF o Imagen)
        civilLiabilityPolicyNumber: '', // Póliza de Responsabilidad Civil (Número)
        poliza_responsabilidad_civil: '', // Póliza de Responsabilidad Civil (PDF o Imagen)
        service: initialService || '',
        foto_perfil: '', // Foto profesional digital
        firma_profesional: '', // Firma profesional digital
    });

    useEffect(() => {

        // Fetch departments when modal opens
        const fetchDepartments = async () => {
            try {
                const response = await apiClient.get(`/api/clinic/departments`);
                setDepartments(response.data);
            } catch (error) {
                console.error('Error fetching departments:', error);
            }
        };

        fetchDepartments();

    }, []);

    useEffect(() => {
        if (formData.department) {
            // Fetch municipalities when a department is selected
            const fetchCities = async () => {
                if (formData.department) {
                    try {
                        const response = await apiClient.get(`/api/clinic/cities/${formData.department}`);
                        setCities(response.data);
                    } catch (error) {
                        console.error('Error fetching cities:', error);
                    }
                }
            };

            fetchCities();
        } else {
            setCities([]);
        }
    }, [formData.department]);

    const handleDepartmentChange = (event) => {
        const selectedId = event.target.value;
        const selectedDepartment = departments.find(dept => dept._id === selectedId);

        setFormData({
            ...formData,
            department: selectedId, // ID del departamento
            departmentName: selectedDepartment ? selectedDepartment.departamento : '', // Nombre del departamento
        });
    };

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'file' ? files[0] : value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Campos que pueden estar vacíos
        const camposOpcionales = ['diploma_especializacion', 'homologacion_estudios'];

        // Determinar qué campos deben estar visibles basándose en el servicio seleccionado
        const visibleFields = Object.keys(formData).filter(field => {
            if (field === 'rethusNumber' || field === 'rethus') {
                return formData.service !== 'abogado' &&
                    formData.service !== 'ingeniero_sistemas' &&
                    formData.service !== 'trabajador_social' &&
                    formData.service !== 'auxiliar_administrativo';
            }
            return true; // Otros campos siempre son visibles
        });

        // Verificar si todos los campos obligatorios de formData tienen datos
        for (const key in formData) {
            if (!formData[key] && !camposOpcionales.includes(key) && visibleFields.includes(key)) {
                alertaGeneral({
                    titulo: `El campo ${key} es obligatorio`,
                    isError: true
                });
                return; // Detener el envío del formulario
            }
        }

        const formDataToSend = new FormData();
        for (const key in formData) {
            formDataToSend.append(key, formData[key]);
        }

        const clinicId = localStorage.getItem('clinicId'); // Obtén el clinicId del localStorage
        formDataToSend.append('clinicId', clinicId);

        try {
            const response = await apiClient.post(`/api/clinic/doctorInfo`, formDataToSend);

            if (response.status >= 200 && response.status < 300) {
                alertaGeneral({
                    titulo: '¡Afiliado creado con éxito!',
                }).then(() => {
                    // Ejecutar onGoBack cuando el usuario haz clic en OK
                    onGoBack();
                })
 
                fetchDoctors();
            } else {
                console.error('Error al guardar los datos:', response.statusText);
            }
        } catch (error) {
            alertaGeneral({
                titulo: 'El correo electrónico ya se encuentra en uso',
                isError: true
            })
        }
    };

    const handleSelectChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value,
        }));
    };

    return (
        <Contenedor icon={Perfil} title="Agregar nuevo profesional">
            <div className="flex items-center justify-end mb-4">
                <button
                    type="button"
                    className="button-gray"
                    onClick={onGoBack}
                >
                    Volver
                </button>
            </div>
            <form onSubmit={handleSubmit} encType="multipart-form-data">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" >
                    {/* Nombre Completo */}
                    <div className="col-span-1">
                        <label className="form-label">Nombre Completo *</label>
                        <input
                            type="text"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Nombre Completo"
                            required
                        />
                    </div>

                    {/* Tipo de Documento de Identidad */}
                    <div className="col-span-1">
                        <label className="form-label">Tipo de Documento de Identidad *</label>
                        <select
                            name="idType"
                            value={formData.idType}
                            onChange={handleSelectChange}
                            className="form-input"
                            required
                        >
                            <option value="">Selecciona</option>
                            <option value="CC">CC</option>
                            <option value="TI">TI</option>
                            <option value="CE">CE</option>
                            <option value="PS">PS</option>
                            <option value="DE">DE</option>
                        </select>
                    </div>

                    {/* Número de Documento de Identidad */}
                    <div className="col-span-1">
                        <label className="form-label">Número de Documento de Identidad *</label>
                        <input
                            type="text"
                            name="idNumber"
                            value={formData.idNumber}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Número de Documento de Identidad"
                            required
                        />
                    </div>

                    {/* Correo Electrónico */}
                    <div className="col-span-1">
                        <label className="form-label">Correo Electrónico *</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Correo Electrónico"
                            required
                        />
                    </div>

                    {/* Número de Celular */}
                    <div className="col-span-1">
                        <label className="form-label">Número de Celular *</label>
                        <input
                            type="text"
                            name="cellNumber"
                            value={formData.cellNumber}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Número de Celular"
                            required
                        />
                    </div>

                    {/* Documento (Adjuntar) */}
                    <div className="col-span-1">
                        <label className="form-label">Documento Identidad (PDF o Imagen) *</label>
                        <InputFile
                            name="cedulaPDF"
                            fileName={formData.cedulaPDF?.name}
                            onChange={handleChange}
                            accept=".pdf,.jpg,.jpeg,.png"
                        />
                    </div>

                    {/* Foto profesional digital */}
                    <div className="col-span-1">
                        <label className="form-label">Foto profesional digital *</label>
                        <InputFile
                            name="foto_perfil"
                            fileName={formData.foto_perfil?.name}
                            onChange={handleChange}
                            accept=".pdf,.jpg,.jpeg,.png"
                        />
                    </div>

                    {/* Tarjeta Profesional */}
                    <div className="col-span-1">
                        <label className="form-label">Tarjeta Profesional (PDF o Imagen)</label>
                        <InputFile
                            name="tarjeta_profesional"
                            fileName={formData.tarjeta_profesional?.name}
                            onChange={handleChange}
                            accept=".pdf,.jpg,.jpeg,.png"
                        />
                    </div>

                    {/* Diploma o Acta de Grado de Pregrado */}
                    <div className="col-span-1">
                        <label className="form-label">Diploma o Acta de Grado de Pregrado (PDF o Imagen)</label>
                        <InputFile
                            name="diploma_acta_grado"
                            fileName={formData.diploma_acta_grado?.name}
                            onChange={handleChange}
                            accept=".pdf,.jpg,.jpeg,.png"
                        />
                    </div>

                    {/* Diploma o Acta de Grado de Especialización */}
                    <div className="col-span-1">
                        <label className="form-label">Diploma o Acta de Grado de Especialización (si aplica)</label>
                        <InputFile
                            name="diploma_especializacion"
                            fileName={formData.diploma_especializacion?.name}
                            onChange={handleChange}
                            accept=".pdf,.jpg,.jpeg,.png"
                        />
                    </div>

                    {/* Homologación de Estudios */}
                    <div className="col-span-1">
                        <label className="form-label">Homologación de Estudios (si aplica)</label>
                        <InputFile
                            name="homologacion_estudios"
                            fileName={formData.homologacion_estudios?.name}
                            onChange={handleChange}
                            accept=".pdf,.jpg,.jpeg,.png"
                        />
                    </div>

                    {formData.service !== 'abogado' &&
                        formData.service !== 'ingeniero_sistemas' &&
                        formData.service !== 'trabajador_social' &&
                        formData.service !== 'auxiliar_administrativo' &&
                        formData.service !== 'cuidador' && (
                            <>
                                {/* Firma del profesional */}
                                <div className="col-span-1">
                                    <label className="form-label">Firma del profesional (300px * 150px)</label>
                                    <InputFile
                                        name="firma_profesional"
                                        fileName={formData.firma_profesional?.name}
                                        onChange={handleChange}
                                        accept=".jpg,.jpeg,.png"
                                    />
                                </div>

                                {/* ReThus */}
                                <div className="grid items-center col-span-1">
                                    <label className="form-label">ReThus (Número y PDF o Imagen) *</label>
                                    <InputFile
                                        name="rethus"
                                        fileName={formData.rethus?.name}
                                        onChange={handleChange}
                                        accept=".pdf,.jpg,.jpeg,.png"
                                    />
                                </div>

                                {/* Póliza de Responsabilidad Civil Vigente */}
                                <div className="col-span-1">
                                    <label className="form-label">Póliza de Responsabilidad Civil Vigente (Número y PDF o Imagen)*</label>
                                    <input
                                        type="text"
                                        name="civilLiabilityPolicyNumber"
                                        required
                                        value={formData.civilLiabilityPolicyNumber}
                                        onChange={handleChange}
                                        className="form-input"
                                        placeholder="Número de Póliza"
                                    />
                                    <InputFile
                                        name="poliza_responsabilidad_civil"
                                        fileName={formData.poliza_responsabilidad_civil?.name}
                                        onChange={handleChange}
                                        accept=".pdf,.jpg,.jpeg,.png"
                                    />
                                </div>
                            </>
                        )}
                </div>

                {/* Botón de Enviar */}
                <div className="flex justify-end mt-4">
                    <button
                        type="submit"
                        className="button-send"
                    >
                        Agregar Profesional
                    </button>
                </div>
            </form >
        </Contenedor >

    );
}

export default DoctorComponent;